/**
 * Help topics of the onboarding package and the glossary (W2-14 onboarding-wizard, content pass W3-08; DESIGN.md
 * §5.14, §2.5), plus the search used by the Help panel and the `?` palette prefix. Other modules add their own topics
 * through `FeatureModule.helpTopics`; the Help panel and the palette list every registered topic.
 *
 * "Show me" targets name a DOM element marked `data-help-id` or a panel / slot (showMe.ts). `SHOW_ME` gives the
 * highlight a text that says what the highlighted part is (UX-094: the bubble showed only the topic title) and,
 * where the bubble would cover the part the text talks about, an area to keep clear. It is keyed by topic id and also
 * covers other modules' topics that have "Show me"; the Help panel reads it with `showMeOf` (change request
 * CR-W3-08-7: HelpTopicDef has no field for it).
 */

import type { HelpTopicDef } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import { m } from './help.messages';
import type { HelpTarget } from './showMe';

/** Ids of this package's topics (stable: commands, links and tests refer to them). */
export const TOPIC = {
  start: 'start',
  timing: 'timing',
  notes: 'notes',
  motion: 'motion',
  layers: 'layers',
  lines: 'lines',
  preview: 'preview',
  testPlay: 'testPlay',
  playback: 'playback',
  tools: 'tools',
  problems: 'problems',
  rpeUsers: 'rpeUsers',
  glossary: 'glossary',
  keyboard: 'keyboard',
} as const;

/**
 * Publishing is explained once, by the Publish module's own topic (src/features/publish, id 'publish'), not by a
 * second article here: two near-identical articles would both come up for "export" or the Publish key.
 */
export const PUBLISH_TOPIC_ID = 'publish';

/**
 * Order of the Help panel's first topics: this package's, with the Publish module's topic where a first chart
 * needs it (after Problems). Topics not listed (other modules') follow in module order. Problems are explained once
 * too, here (the checker module has no article of its own).
 */
export const HELP_ORDER: readonly string[] = [
  TOPIC.start,
  TOPIC.timing,
  TOPIC.notes,
  TOPIC.motion,
  TOPIC.layers,
  TOPIC.lines,
  TOPIC.preview,
  TOPIC.testPlay,
  TOPIC.playback,
  TOPIC.tools,
  TOPIC.problems,
  PUBLISH_TOPIC_ID,
  TOPIC.rpeUsers,
  TOPIC.glossary,
  TOPIC.keyboard,
];

export const HELP_TOPICS: readonly HelpTopicDef[] = [
  { id: TOPIC.start, title: m.startTitle, body: m.startBody, level: 'guided' },
  { id: TOPIC.timing, title: m.timingTitle, body: m.timingBody, showMe: { panel: 'timing.left' }, level: 'guided' },
  { id: TOPIC.notes, title: m.notesTitle, body: m.notesBody, showMe: { helpId: 'tool-rail' }, level: 'guided' },
  { id: TOPIC.motion, title: m.motionTitle, body: m.motionBody, showMe: { helpId: 'motion-columns' }, level: 'guided' },
  { id: TOPIC.layers, title: m.layersTitle, body: m.layersBody, showMe: { helpId: 'motion-columns' }, level: 'full' },
  { id: TOPIC.lines, title: m.linesTitle, body: m.linesBody, showMe: { helpId: 'lines.panel' }, level: 'guided' },
  {
    id: TOPIC.preview,
    title: m.previewTitle,
    body: m.previewBody,
    showMe: { helpId: 'preview-stage' },
    level: 'guided',
  },
  {
    id: TOPIC.testPlay,
    title: m.testPlayTitle,
    body: m.testPlayBody,
    showMe: { helpId: 'preview-panel' },
    level: 'guided',
  },
  {
    id: TOPIC.playback,
    title: m.playbackTitle,
    body: m.playbackBody,
    showMe: { helpId: 'transport.play' },
    level: 'guided',
  },
  { id: TOPIC.tools, title: m.toolsTitle, body: m.toolsBody, showMe: { helpId: 'tool-rail' }, level: 'full' },
  {
    id: TOPIC.problems,
    title: m.problemsTitle,
    body: m.problemsBody,
    showMe: { helpId: 'problems.chip' },
    level: 'guided',
  },
  {
    id: TOPIC.rpeUsers,
    title: m.rpeTitle,
    body: m.rpeBody,
    showMe: { helpId: 'motion-columns' },
    level: 'guided',
  },
  { id: TOPIC.glossary, title: m.glossaryTitle, body: m.glossaryBody, level: 'guided' },
  { id: TOPIC.keyboard, title: m.keyboardTitle, body: m.keyboardBody, level: 'guided' },
];

/** What a "Show me" highlight says, and the part of the editor its bubble keeps clear of. */
export interface ShowMeText {
  readonly body: MessageKey;
  readonly avoid?: HelpTarget;
}

/** "Show me" texts by topic id: this package's topics and the other modules' topics that have "Show me". */
export const SHOW_ME: Readonly<Record<string, ShowMeText>> = {
  [TOPIC.timing]: { body: m.showMeTiming },
  [TOPIC.notes]: { body: m.showMeNotes, avoid: { helpId: 'timeline' } },
  [TOPIC.motion]: { body: m.showMeMotion, avoid: { helpId: 'timeline' } },
  [TOPIC.layers]: { body: m.showMeLayers, avoid: { helpId: 'timeline' } },
  [TOPIC.lines]: { body: m.showMeLines },
  [TOPIC.preview]: { body: m.showMePreview },
  [TOPIC.testPlay]: { body: m.showMeTestPlay },
  [TOPIC.playback]: { body: m.showMePlayback },
  [TOPIC.tools]: { body: m.showMeTools, avoid: { helpId: 'timeline' } },
  [TOPIC.problems]: { body: m.showMeProblems },
  [TOPIC.rpeUsers]: { body: m.showMeRpe, avoid: { helpId: 'timeline' } },
  publish: { body: m.showMePublish },
  'note-lane': { body: m.showMeLane },
  'curve-editor': { body: m.showMeCurveEditor },
  'motion.columns': { body: m.showMeMotion, avoid: { helpId: 'timeline' } },
  'lines.panel': { body: m.showMeLinesPanel },
  'lines.parents': { body: m.showMeLinesPanel },
  'preview.overview': { body: m.showMePreviewOverview },
  'preview.gizmo': { body: m.showMeGizmo },
  history: { body: m.showMeHistory },
  assets: { body: m.showMeAssets },
  'inspector.expressions': { body: m.showMeInspector },
  'timing.setTiming': { body: m.showMeTiming },
  'timing.meter': { body: m.showMeTiming },
  'timing.latency': { body: m.showMeLatency },
};

/** The "Show me" text of a topic (null: the highlight shows the title only). */
export function showMeOf(topicId: string): ShowMeText | null {
  return SHOW_ME[topicId] ?? null;
}

/** One glossary word (D§2.5 vocabulary): the label used in Phiedit and its RPE / community terms. */
export interface GlossaryEntry {
  readonly id: string;
  readonly term: string;
  readonly detail: string;
}

export const GLOSSARY: readonly GlossaryEntry[] = [
  { id: 'line', term: m.gLine, detail: m.gLineDetail },
  { id: 'keyframe', term: m.gKeyframe, detail: m.gKeyframeDetail },
  { id: 'curve', term: m.gCurve, detail: m.gCurveDetail },
  { id: 'slice', term: m.gSlice, detail: m.gSliceDetail },
  { id: 'stay', term: m.gStay, detail: m.gStayDetail },
  { id: 'jump', term: m.gJump, detail: m.gJumpDetail },
  { id: 'layer', term: m.gLayer, detail: m.gLayerDetail },
  { id: 'speed', term: m.gSpeed, detail: m.gSpeedDetail },
  { id: 'distance', term: m.gDistance, detail: m.gDistanceDetail },
  { id: 'cover', term: m.gCover, detail: m.gCoverDetail },
  { id: 'parent', term: m.gParent, detail: m.gParentDetail },
  { id: 'rotateParent', term: m.gRotateParent, detail: m.gRotateParentDetail },
  { id: 'zOrder', term: m.gZOrder, detail: m.gZOrderDetail },
  { id: 'side', term: m.gSide, detail: m.gSideDetail },
  { id: 'fake', term: m.gFake, detail: m.gFakeDetail },
  { id: 'width', term: m.gWidth, detail: m.gWidthDetail },
  { id: 'yOffset', term: m.gYOffset, detail: m.gYOffsetDetail },
  { id: 'visible', term: m.gVisible, detail: m.gVisibleDetail },
  { id: 'uiMover', term: m.gUiMover, detail: m.gUiMoverDetail },
  { id: 'tilt', term: m.gTilt, detail: m.gTiltDetail },
  { id: 'controls', term: m.gControls, detail: m.gControlsDetail },
  { id: 'linked', term: m.gLinked, detail: m.gLinkedDetail },
  { id: 'continue', term: m.gContinue, detail: m.gContinueDetail },
  { id: 'extrapolate', term: m.gExtrapolate, detail: m.gExtrapolateDetail },
  { id: 'bake', term: m.gBake, detail: m.gBakeDetail },
  { id: 'simultaneous', term: m.gSimultaneous, detail: m.gSimultaneousDetail },
  { id: 'beat', term: m.gBeat, detail: m.gBeatDetail },
];

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

/** Text of a topic for searching: translated title and body (plain text of the body). */
export interface SearchableTopic {
  readonly topic: HelpTopicDef;
  readonly title: string;
  readonly body: string;
}

function norm(s: string): string {
  return s.toLowerCase().normalize('NFKC');
}

/**
 * Topics matching `query`, best first: every word of the query must occur in the title, the body or the id; a
 * title match ranks above a body match, the whole query in the title highest. An empty query keeps the order.
 */
export function searchTopics(topics: readonly SearchableTopic[], query: string): SearchableTopic[] {
  const q = norm(query.trim());
  if (q === '') return [...topics];
  const words = q.split(/\s+/).filter((w) => w !== '');
  const scored: Array<{ readonly t: SearchableTopic; readonly score: number; readonly i: number }> = [];
  topics.forEach((t, i) => {
    const title = norm(t.title);
    const body = norm(t.body);
    const id = norm(t.topic.id);
    let score = title.includes(q) ? 100 : 0;
    for (const w of words) {
      if (title.includes(w)) score += title.split(/\s+/).some((x) => x.startsWith(w)) ? 30 : 20;
      else if (id.includes(w)) score += 15;
      else if (body.includes(w)) score += 5;
      else return;
    }
    scored.push({ t, score, i });
  });
  return scored.sort((a, b) => b.score - a.score || a.i - b.i).map((x) => x.t);
}

/** Glossary entries matching `query` (term or detail contains every word). */
export function searchGlossary(
  entries: ReadonlyArray<{ readonly entry: GlossaryEntry; readonly term: string; readonly detail: string }>,
  query: string,
): Array<{ readonly entry: GlossaryEntry; readonly term: string; readonly detail: string }> {
  const words = norm(query.trim())
    .split(/\s+/)
    .filter((w) => w !== '');
  if (words.length === 0) return [];
  return entries.filter((e) => {
    const hay = `${norm(e.term)} ${norm(e.detail)}`;
    return words.every((w) => hay.includes(w));
  });
}
